import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { ICON_MAP } from '../../utils/iconMap';

export default function MenuGroupModal({ isOpen, onClose, group, onSave }) {
  const supabase = useSupabaseClient();
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    tooltip: '',
  });

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        icon: group.icon || '',
        tooltip: group.tooltip || '',
      });
    } else {
      setFormData({
        name: '',
        icon: '',
        tooltip: '',
      });
    }
  }, [group]);

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.icon) {
        toast({
          title: 'Validation Error',
          description: 'Name and icon are required',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      const { data: lastGroup } = await supabase
        .from('menu_groups')
        .select('display_order')
        .order('display_order', { ascending: false })
        .limit(1)
        .single();

      const newDisplayOrder = lastGroup ? lastGroup.display_order + 10 : 10;

      const { error } = await supabase
        .from('menu_groups')
        .upsert({
          id: group?.id,
          name: formData.name,
          icon: formData.icon,
          tooltip: formData.tooltip,
          display_order: group ? group.display_order : newDisplayOrder,
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Menu group ${group ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving menu group:', error);
      toast({
        title: 'Error saving menu group',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {group ? 'Edit Menu Group' : 'Create Menu Group'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Group Name</FormLabel>
              <FormHelperText mb={2}>Enter a descriptive name for this menu group</FormHelperText>
              <Input
                placeholder="e.g., Messages, Settings"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Icon</FormLabel>
              <FormHelperText mb={2}>Select an icon to represent this group</FormHelperText>
              <Select
                placeholder="Choose an icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {Object.keys(ICON_MAP).map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Tooltip</FormLabel>
              <FormHelperText mb={2}>Add a helpful tooltip that appears when users hover over this group</FormHelperText>
              <Input
                placeholder="e.g., Manage all message-related functions"
                value={formData.tooltip}
                onChange={(e) => setFormData({ ...formData, tooltip: e.target.value })}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
